/*
 * /details/$patientId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Page,
	Placeholder,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { ArrowDownLeft, ArrowUpRight, Pencil, Plus, Trash2 } from "lucide-react"
import { PatientForm } from "../../features/patient/patient-form"
import { AddressForm } from "../../features/address/address-form"
import { PatientHeader } from "../../features/patient/patient-header"
import { trpc } from "../../trpc"
import type { Address, PatientDiagnosis, PatientReferral } from "@emr/db/client"
import { DIAGNOSIS_TYPES, REFERRAL_DIRECTIONS } from "@emr/db/client"

export const Route = createFileRoute("/details/$patientId")({
	component: DetailsPage,
})

const MOCK_PATIENT = {
	firstName: "...",
	lastName: "",
	dateOfBirth: null,
	bloodType: null,
	allergies: null,
	emergencyContactName: null,
	emergencyContactPhone: null,
}

const ADDRESS_TYPE_LABELS: Record<string, string> = {
	home: "Home",
	work: "Work",
	billing: "Billing",
	shipping: "Shipping",
	other: "Other",
}

function DetailsPage() {
	const { patientId } = Route.useParams()
	const utils = trpc.useUtils()

	const [editingAddressId, setEditingAddressId] = useState<string | null>(null)
	const [showAddAddress, setShowAddAddress] = useState(false)

	// Diagnosis dialog state
	const [diagnosisDialogOpen, setDiagnosisDialogOpen] = useState(false)
	const [editingDiagnosis, setEditingDiagnosis] = useState<PatientDiagnosis | null>(null)
	const [dxDiagnosisId, setDxDiagnosisId] = useState("")
	const [dxType, setDxType] = useState<"primary" | "secondary">("secondary")
	const [dxYear, setDxYear] = useState("")

	// Referral dialog state
	const [referralDialogOpen, setReferralDialogOpen] = useState(false)
	const [editingReferral, setEditingReferral] = useState<PatientReferral | null>(null)
	const [refReferralId, setRefReferralId] = useState("")
	const [refDirection, setRefDirection] = useState<"incoming" | "outgoing">("outgoing")
	const [refDate, setRefDate] = useState("")
	const [refNotes, setRefNotes] = useState("")

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: addresses, isLoading: addressesLoading } =
		trpc.address.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: patientDiagnoses } =
		trpc.patientDiagnosis.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: allDiagnoses } =
		trpc.diagnosis.getAllByFilter.useQuery({ where: { active: true } })

	const { data: patientReferrals } =
		trpc.patientReferral.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: allReferrals } =
		trpc.referral.getAllByFilter.useQuery({ where: { active: true } })

	const deleteAddress = trpc.address.update.useMutation({
		onSuccess: () => {
			utils.address.getAllByFilter.invalidate()
			toast.success("Address removed")
		},
	})

	const createDiagnosis = trpc.patientDiagnosis.create.useMutation({
		onSuccess: () => {
			utils.patientDiagnosis.getAllByFilter.invalidate()
			toast.success("Diagnosis added")
			closeDiagnosisDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const updateDiagnosis = trpc.patientDiagnosis.update.useMutation({
		onSuccess: () => {
			utils.patientDiagnosis.getAllByFilter.invalidate()
			toast.success("Diagnosis updated")
			closeDiagnosisDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const removeDiagnosis = trpc.patientDiagnosis.update.useMutation({
		onSuccess: () => {
			utils.patientDiagnosis.getAllByFilter.invalidate()
			toast.success("Diagnosis removed")
		},
		onError: (e) => toast.error(e.message),
	})

	const createReferral = trpc.patientReferral.create.useMutation({
		onSuccess: () => {
			utils.patientReferral.getAllByFilter.invalidate()
			toast.success("Referral added")
			closeReferralDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const updateReferral = trpc.patientReferral.update.useMutation({
		onSuccess: () => {
			utils.patientReferral.getAllByFilter.invalidate()
			toast.success("Referral updated")
			closeReferralDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const removeReferral = trpc.patientReferral.update.useMutation({
		onSuccess: () => {
			utils.patientReferral.getAllByFilter.invalidate()
			toast.success("Referral removed")
		},
		onError: (e) => toast.error(e.message),
	})

	const diagnosisMap = Object.fromEntries((allDiagnoses ?? []).map((d) => [d.id, d]))
	const referralMap = Object.fromEntries((allReferrals ?? []).map((r) => [r.id, r]))

	function openAddDiagnosis() {
		setEditingDiagnosis(null)
		setDxDiagnosisId("")
		setDxType("secondary")
		setDxYear("")
		setDiagnosisDialogOpen(true)
	}
	function openEditDiagnosis(pd: PatientDiagnosis) {
		setEditingDiagnosis(pd)
		setDxDiagnosisId(pd.diagnosisId)
		setDxType((pd.type as "primary" | "secondary") ?? "secondary")
		setDxYear(pd.yearDiagnosed ? String(pd.yearDiagnosed) : "")
		setDiagnosisDialogOpen(true)
	}
	function closeDiagnosisDialog() {
		setDiagnosisDialogOpen(false)
		setEditingDiagnosis(null)
	}
	function saveDiagnosis() {
		if (!dxDiagnosisId) return
		const year = dxYear ? parseInt(dxYear, 10) : undefined
		if (editingDiagnosis) {
			updateDiagnosis.mutate({
				id: editingDiagnosis.id,
				diagnosisId: dxDiagnosisId,
				type: dxType,
				yearDiagnosed: year ?? null,
			})
		} else {
			createDiagnosis.mutate({
				patientId,
				diagnosisId: dxDiagnosisId,
				type: dxType,
				yearDiagnosed: year,
			})
		}
	}

	function openAddReferral() {
		setEditingReferral(null)
		setRefReferralId("")
		setRefDirection("outgoing")
		setRefDate("")
		setRefNotes("")
		setReferralDialogOpen(true)
	}
	function openEditReferral(pr: PatientReferral) {
		setEditingReferral(pr)
		setRefReferralId(pr.referralId)
		setRefDirection((pr.direction as "incoming" | "outgoing") ?? "outgoing")
		setRefDate(pr.referralDate ?? "")
		setRefNotes(pr.notes ?? "")
		setReferralDialogOpen(true)
	}
	function closeReferralDialog() {
		setReferralDialogOpen(false)
		setEditingReferral(null)
	}
	function saveReferral() {
		if (!refReferralId) return
		if (editingReferral) {
			updateReferral.mutate({
				id: editingReferral.id,
				referralId: refReferralId,
				direction: refDirection,
				referralDate: refDate || null,
				notes: refNotes || null,
			})
		} else {
			createReferral.mutate({
				patientId,
				referralId: refReferralId,
				direction: refDirection,
				referralDate: refDate || undefined,
				notes: refNotes || undefined,
			})
		}
	}

	const isLoading = patientLoading || addressesLoading
	const displayPatient = patient ?? (MOCK_PATIENT as any)

	const diagnosisSaving = createDiagnosis.isPending || updateDiagnosis.isPending
	const referralSaving = createReferral.isPending || updateReferral.isPending

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={false} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<>
						{/* Personal information */}
						<Card>
							<CardHeader>
								<CardTitle>Personal Information</CardTitle>
							</CardHeader>
							<CardContent>
								<PatientForm patient={patient ?? undefined} />
							</CardContent>
						</Card>

						{/* Addresses */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>Addresses</CardTitle>
									{!showAddAddress && (
										<Button
											size="sm"
											variant="outline"
											onClick={() => {
												setEditingAddressId(null)
												setShowAddAddress(true)
											}}
										>
											<Plus className="w-4 h-4 mr-1" />
											Add Address
										</Button>
									)}
								</div>
							</CardHeader>
							<CardContent className="flex flex-col gap-4">
								{showAddAddress && (
									<div className="border rounded-lg p-4">
										<p className="text-sm font-medium mb-3">New Address</p>
										<AddressForm
											patientId={patientId}
											onSuccess={() => {
												setShowAddAddress(false)
												utils.address.getAllByFilter.invalidate()
											}}
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="mt-2"
											onClick={() => setShowAddAddress(false)}
										>
											Cancel
										</Button>
									</div>
								)}
								{!addresses || addresses.length === 0 ? (
									!showAddAddress && (
										<p className="text-sm text-muted-foreground">No addresses on file.</p>
									)
								) : (
									addresses.map((address: Address) => (
										<AddressRow
											key={address.id}
											address={address}
											isEditing={editingAddressId === address.id}
											onEdit={() => {
												setShowAddAddress(false)
												setEditingAddressId(address.id)
											}}
											onEditDone={() => {
												setEditingAddressId(null)
												utils.address.getAllByFilter.invalidate()
											}}
											onDelete={() =>
												deleteAddress.mutate({ id: address.id, active: false })
											}
										/>
									))
								)}
							</CardContent>
						</Card>

						{/* Diagnoses */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>Diagnoses</CardTitle>
									<Button size="sm" variant="outline" onClick={openAddDiagnosis}>
										<Plus className="w-4 h-4 mr-1" /> Add Diagnosis
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								{!patientDiagnoses || patientDiagnoses.length === 0 ? (
									<p className="text-sm text-muted-foreground">No diagnoses on file.</p>
								) : (
									<div className="divide-y rounded-lg border">
										{patientDiagnoses.map((pd) => {
											const dx = diagnosisMap[pd.diagnosisId]
											return (
												<div key={pd.id} className="flex items-center gap-3 px-4 py-3">
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 flex-wrap">
															<span className="text-sm font-medium">{dx?.name ?? "Unknown"}</span>
															<Badge variant={pd.type === "primary" ? "default" : "secondary"} className="text-xs capitalize">
																{pd.type}
															</Badge>
															{pd.yearDiagnosed && (
																<span className="text-xs text-muted-foreground">Diagnosed {pd.yearDiagnosed}</span>
															)}
														</div>
													</div>
													<div className="flex items-center gap-1 shrink-0">
														<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDiagnosis(pd)}>
															<Pencil className="w-3.5 h-3.5" />
														</Button>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-destructive hover:text-destructive"
															onClick={() => removeDiagnosis.mutate({ id: pd.id, active: false })}
														>
															<Trash2 className="w-3.5 h-3.5" />
														</Button>
													</div>
												</div>
											)
										})}
									</div>
								)}
							</CardContent>
						</Card>

						{/* Referrals */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>Referrals</CardTitle>
									<Button size="sm" variant="outline" onClick={openAddReferral}>
										<Plus className="w-4 h-4 mr-1" /> Add Referral
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								{!patientReferrals || patientReferrals.length === 0 ? (
									<p className="text-sm text-muted-foreground">No referrals on file.</p>
								) : (
									<div className="divide-y rounded-lg border">
										{patientReferrals.map((pr) => {
											const ref = referralMap[pr.referralId]
											return (
												<div key={pr.id} className="flex items-center gap-3 px-4 py-3">
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 flex-wrap">
															{pr.direction === "incoming" ? (
																<ArrowDownLeft className="w-4 h-4 text-blue-500 shrink-0" />
															) : (
																<ArrowUpRight className="w-4 h-4 text-green-500 shrink-0" />
															)}
															<span className="text-sm font-medium">{ref?.companyName ?? "Unknown"}</span>
															<Badge variant="outline" className="text-xs capitalize">
																{pr.direction}
															</Badge>
															{pr.referralDate && (
																<span className="text-xs text-muted-foreground">{pr.referralDate}</span>
															)}
														</div>
														{pr.notes && (
															<p className="text-xs text-muted-foreground mt-1 truncate">{pr.notes}</p>
														)}
													</div>
													<div className="flex items-center gap-1 shrink-0">
														<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditReferral(pr)}>
															<Pencil className="w-3.5 h-3.5" />
														</Button>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-destructive hover:text-destructive"
															onClick={() => removeReferral.mutate({ id: pr.id, active: false })}
														>
															<Trash2 className="w-3.5 h-3.5" />
														</Button>
													</div>
												</div>
											)
										})}
									</div>
								)}
							</CardContent>
						</Card>

						{/* Primary Care Provider — placeholder */}
						<PlaceholderCard
							title="Primary Care Provider"
							description="Primary care provider details coming soon."
						/>
					</>
				)}
			</Container>

			{/* Diagnosis dialog */}
			<Dialog open={diagnosisDialogOpen} onOpenChange={(o) => { if (!o) closeDiagnosisDialog() }}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{editingDiagnosis ? "Edit Diagnosis" : "Add Diagnosis"}</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-1">
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Diagnosis <span className="text-destructive">*</span></label>
							<Select value={dxDiagnosisId} onValueChange={setDxDiagnosisId}>
								<SelectTrigger>
									<SelectValue placeholder="Select diagnosis…" />
								</SelectTrigger>
								<SelectContent>
									{(allDiagnoses ?? []).map((d) => (
										<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Type</label>
							<Select value={dxType} onValueChange={(v) => setDxType(v as "primary" | "secondary")}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{DIAGNOSIS_TYPES.map((t) => (
										<SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Year Diagnosed</label>
							<Input
								type="number"
								placeholder="e.g. 2021"
								value={dxYear}
								onChange={(e) => setDxYear(e.target.value)}
								min={1900}
								max={new Date().getFullYear()}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDiagnosisDialog}>Cancel</Button>
						<Button onClick={saveDiagnosis} disabled={!dxDiagnosisId || diagnosisSaving}>
							{diagnosisSaving ? "Saving…" : "Save"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Referral dialog */}
			<Dialog open={referralDialogOpen} onOpenChange={(o) => { if (!o) closeReferralDialog() }}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{editingReferral ? "Edit Referral" : "Add Referral"}</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-1">
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Referral Source <span className="text-destructive">*</span></label>
							<Select value={refReferralId} onValueChange={setRefReferralId}>
								<SelectTrigger>
									<SelectValue placeholder="Select referral…" />
								</SelectTrigger>
								<SelectContent>
									{(allReferrals ?? []).map((r) => (
										<SelectItem key={r.id} value={r.id}>{r.companyName}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Direction</label>
							<Select value={refDirection} onValueChange={(v) => setRefDirection(v as "incoming" | "outgoing")}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{REFERRAL_DIRECTIONS.map((d) => (
										<SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Date of Referral</label>
							<Input
								type="date"
								value={refDate}
								onChange={(e) => setRefDate(e.target.value)}
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Notes</label>
							<Textarea
								placeholder="Any notes about this referral…"
								value={refNotes}
								onChange={(e) => setRefNotes(e.target.value)}
								rows={3}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeReferralDialog}>Cancel</Button>
						<Button onClick={saveReferral} disabled={!refReferralId || referralSaving}>
							{referralSaving ? "Saving…" : "Save"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Page>
	)
}

/* ─── Address row ────────────────────────────────────────────────────────── */

function AddressRow({
	address,
	isEditing,
	onEdit,
	onEditDone,
	onDelete,
}: {
	address: Address
	isEditing: boolean
	onEdit: () => void
	onEditDone: () => void
	onDelete: () => void
}) {
	const label = ADDRESS_TYPE_LABELS[address.type] ?? address.type

	if (isEditing) {
		return (
			<div className="border rounded-lg p-4">
				<p className="text-sm font-medium mb-3">{label}</p>
				<AddressForm entity={address} onSuccess={onEditDone} />
				<Button type="button" variant="ghost" size="sm" className="mt-2" onClick={onEditDone}>
					Cancel
				</Button>
			</div>
		)
	}

	return (
		<div className="border rounded-lg p-4 flex items-start justify-between gap-4">
			<div className="flex flex-col gap-1 text-sm">
				<div className="flex items-center gap-2">
					<Badge variant="secondary" className="text-xs capitalize">{label}</Badge>
				</div>
				<p className="mt-1">{address.line1}</p>
				{address.line2 && <p>{address.line2}</p>}
				<p>
					{address.city}
					{address.state ? `, ${address.state}` : ""}{" "}
					{address.postalCode}
				</p>
				<p className="text-muted-foreground">{address.country}</p>
			</div>
			<div className="flex gap-2 shrink-0">
				<Button variant="outline" size="sm" onClick={onEdit}>Edit</Button>
				<Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={onDelete}>
					Remove
				</Button>
			</div>
		</div>
	)
}

/* ─── Placeholder card ───────────────────────────────────────────────────── */

function PlaceholderCard({ title, description }: { title: string; description: string }) {
	return (
		<Card>
			<CardHeader><CardTitle>{title}</CardTitle></CardHeader>
			<CardContent>
				<p className="text-sm text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	)
}
